/*
 * Cliente del antibot.
 *
 * Reglas que no se pueden romper:
 * - El challenge se pide en la PRIMERA interaccion, no en la carga: asi la
 *   pagina sigue siendo cacheable (ni MicroPageCache ni el fastcgi_cache
 *   discriminan por visitante, asi que un challenge horneado en el HTML se
 *   serviria compartido y el primero que enviara lo consumiria para todos) y
 *   ademas el tiempo minimo mide lo que importa: lo que se tarda en rellenar,
 *   no lo que la pestana lleva abierta.
 * - Nada de comentarios de linea dentro de atributos HTML: el middleware
 *   MinifyHtml aplasta el HTML final —incluidos los atributos, que no protege
 *   como si hace con <script> y <style>— y un // se comeria el resto.
 * - Si un auto-submit se dispara con el challenge en vuelo, se ENCOLA el envio
 *   hasta que la promesa resuelva, en lugar de salir sin token.
 * - Cuando se encola, ademas de preventDefault() hay que llamar a
 *   stopImmediatePropagation(): preventDefault NO detiene a los demas listeners
 *   del mismo elemento, asi que sin el, un formulario con su propio manejador
 *   de submit lo ejecutaria en la interceptacion Y otra vez en el reenvio. Es
 *   decir, envio duplicado. Este modulo se engancha ANTES que los demas bundles
 *   (ver el final del fichero), que es lo que hace que detenerlos funcione.
 *
 * PRUEBA DE TRABAJO. El servidor manda `pow_salt` y `difficulty_bits` con el
 * challenge, y aqui se busca un `nonce` tal que
 * sha256("{challenge_id}.{pow_salt}.{nonce}") empiece por esos bits a cero. Tres
 * reglas, y ninguna es opcional:
 *
 * - Se arranca AL PEDIR el challenge, no al enviar. Con <=16 bits el nonce esta
 *   listo mucho antes de que una persona termine de escribir.
 * - Si el envio llega antes, se ENCOLA. Nunca se cuelga la interfaz: el trabajo
 *   vive en workers y, si no los hay, va troceado con cesion del hilo.
 * - Tope duro de 20 s: pasado ese tiempo se envia SIN nonce y decide el
 *   servidor. Fallar cerrado aqui seria dejar fuera al movil lento, que es
 *   justo a quien esto no puede castigar.
 *
 * El esquema del token (v) es el de config/antibot.php (schema_version). El
 * verificador acepta la version actual y la anterior mientras
 * accept_legacy_schema siga en true, que es lo que da margen para desplegar
 * este fichero y el servidor en cualquier orden.
 *
 * ═══ ESTE FICHERO TIENE UN ESPEJO EXACTO EN public/js/antibot-form.js ═══
 *
 * Las landings de Commerce (site/comercio/layout.blade.php) y de Verify
 * (saas/services/otp/public/layout.blade.php) son standalone: su layout NO
 * carga NINGUN bundle de Vite —ni almc.js ni public-core.js—, asi que por esta
 * via el modulo no llega a sus formularios y cada envio saldria sin token.
 * Comprobado renderizando por HTTP, no suponiendo: ninguna de las dos paginas
 * emite un solo /build/ en su HTML.
 *
 * La alternativa limpia —una entrada propia en vite.config.js— exige
 * reconstruir el manifest para que esas dos paginas se puedan siquiera
 * RENDERIZAR (@vite lanza si la entrada no esta en el manifest), asi que
 * cualquier arbol con un build viejo se quedaria sin poder abrirlas. Por eso
 * se sirve como <script> clasico desde public/, igual que ya hace ese layout
 * con Alpine (public/vendor/alpinejs-*.js).
 *
 * Los dos ficheros deben tener el MISMO CONTENIDO, y lo fija
 * tests/Feature/AntiBot/CommerceDemoWiringTest.php. Si tocas este:
 *   cp resources/js/antibot/antibot-form.js public/js/antibot-form.js
 *
 * Lo mismo vale para el worker de la prueba de trabajo, que ademas se carga POR
 * URL y por tanto TIENE que existir bajo public/ pase lo que pase con Vite
 * (PowTest.php lo fija):
 *   cp resources/js/antibot/antibot-pow.worker.js public/js/antibot-pow.worker.js
 */
(function () {
  'use strict';

  var SEED_URL = '/_ab/seed';
  var SCHEMA_VERSION = 2;

  /*
   * El worker se carga POR URL, no por import: la ruta tiene que existir
   * servida desde public/ tanto si la pagina vino por un bundle de Vite como si
   * es una landing standalone. Su fuente canonica esta en
   * resources/js/antibot/antibot-pow.worker.js y hay un test que impide que las
   * dos copias diverjan.
   */
  var POW_WORKER_URL = '/js/antibot-pow.worker.js';

  /* Tope duro. Pasado esto se envia SIN nonce: decide el servidor. */
  var POW_TIMEOUT_MS = 20000;

  /* Tope del pool. Mas alla de 4 solo se calienta el movil de un usuario legitimo. */
  var POW_MAX_WORKERS = 4;

  /* Hashes por tanda en el repliegue de hilo principal, entre cesion y cesion. */
  var POW_TANDA = 64;

  var CODIFICADOR = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

  function issue(seedUrl, profile, route) {
    return fetch(seedUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ profile: profile, route: route })
    }).then(function (r) {
      if (!r.ok) throw new Error('seed ' + r.status);
      return r.json();
    });
  }

  function encodeToken(state) {
    return btoa(JSON.stringify({
      v: SCHEMA_VERSION,
      challenge_id: state.challengeId,
      seed: state.seed,
      nonce: state.nonce || ''
    }));
  }

  /*
   * ¿El digest empieza por al menos `bits` bits a cero?
   *
   * 🚨 Duplicada a proposito en tres sitios: aqui, en el worker (que no puede
   * importar de este modulo) y en el verificador de PHP. Si se toca una, hay que
   * tocar las tres.
   */
  function cumpleBits(bytes, bits) {
    var enteros = bits >> 3;

    for (var i = 0; i < enteros; i++) {
      if (bytes[i] !== 0) return false;
    }

    var sueltos = bits & 7;

    if (sueltos === 0) return true;

    return (bytes[enteros] >> (8 - sueltos)) === 0;
  }

  function haySubtle() {
    return !!(CODIFICADOR && window.crypto && window.crypto.subtle);
  }

  /*
   * Pool de workers. Uno por hilo disponible, con tope de 4, y cada uno recorre
   * una progresion distinta (start = i, step = n) para no repetir intentos.
   *
   * RECHAZA si los workers no arrancan —CSP, fichero que no esta, navegador sin
   * Worker— para que quien llama pueda degradar. Nunca resuelve con error.
   */
  function conWorkers(prefijo, bits, limite) {
    return new Promise(function (resolve, reject) {
      if (typeof Worker !== 'function') {
        reject(new Error('sin worker'));

        return;
      }

      var cuantos = Math.min(navigator.hardwareConcurrency || 2, POW_MAX_WORKERS);
      var pool = [];
      var vivos = 0;
      var cerrado = false;

      function cerrar() {
        cerrado = true;

        for (var i = 0; i < pool.length; i++) {
          try { pool[i].terminate(); } catch (err) { /* ya estaba muerto */ }
        }

        pool = [];
      }

      for (var i = 0; i < cuantos; i++) {
        var w;

        try {
          w = new Worker(POW_WORKER_URL);
        } catch (err) {
          cerrar();
          reject(err);

          return;
        }

        /*
         * Un worker bloqueado por CSP o un 404 del fichero no lanzan aqui: se
         * enteran por onerror, y a veces despues de que otro haya respondido.
         * De ahi la bandera.
         */
        w.onerror = function () {
          if (cerrado) return;

          cerrar();
          reject(new Error('worker'));
        };

        w.onmessage = function (ev) {
          if (cerrado) return;

          var nonce = ev.data && ev.data.nonce;

          if (nonce) {
            cerrar();
            resolve(String(nonce));

            return;
          }

          vivos--;

          if (vivos <= 0) {
            cerrar();
            resolve('');
          }
        };

        pool.push(w);
        vivos++;
        w.postMessage({ prefix: prefijo, bits: bits, start: i, step: cuantos, deadline: limite });
      }
    });
  }

  /*
   * Repliegue: el mismo trabajo en el hilo principal, TROCEADO.
   *
   * Existe porque la CSP de este sitio es dinamica y viene de base de datos: un
   * worker bloqueado alli —y solo alli— no puede convertirse en un formulario
   * que no envia. Se cede el hilo entre tandas con setTimeout(0) para que la
   * pagina siga respondiendo mientras busca.
   */
  function enHiloPrincipal(prefijo, bits, limite) {
    return new Promise(function (resolve) {
      if (!haySubtle()) {
        resolve('');

        return;
      }

      var nonce = 0;

      function tanda() {
        if (Date.now() > limite) {
          resolve('');

          return;
        }

        var tareas = [];

        for (var i = 0; i < POW_TANDA; i++) {
          tareas.push(intento(nonce));
          nonce++;
        }

        Promise.all(tareas).then(function (resultados) {
          for (var j = 0; j < resultados.length; j++) {
            if (resultados[j] !== null) {
              resolve(String(resultados[j]));

              return;
            }
          }

          setTimeout(tanda, 0);
        }, function () {
          resolve('');
        });
      }

      function intento(n) {
        return window.crypto.subtle.digest('SHA-256', CODIFICADOR.encode(prefijo + n)).then(function (buffer) {
          return cumpleBits(new Uint8Array(buffer), bits) ? n : null;
        });
      }

      tanda();
    });
  }

  /*
   * Nonce del challenge, o cadena vacia si no hace falta o no se ha podido.
   *
   * Nunca rechaza y nunca tarda mas de POW_TIMEOUT_MS: el tope es una carrera
   * contra un temporizador, asi que ni un worker colgado ni una CSP rara pueden
   * dejar un formulario sin enviar.
   */
  function resolverPow(data) {
    var bits = (data && data.difficulty_bits) | 0;

    if (!bits || !data.pow_salt || !haySubtle()) return Promise.resolve('');

    var limite = Date.now() + POW_TIMEOUT_MS;
    var prefijo = data.challenge_id + '.' + data.pow_salt + '.';

    var trabajo = conWorkers(prefijo, bits, limite).catch(function () {
      return enHiloPrincipal(prefijo, bits, limite);
    }).catch(function () {
      return '';
    });

    var tope = new Promise(function (resolve) {
      setTimeout(function () { resolve(''); }, POW_TIMEOUT_MS);
    });

    return Promise.race([trabajo, tope]);
  }

  function attach(form, opts) {
    if (!form || form.__antibotAttached) return;
    form.__antibotAttached = true;

    opts = opts || {};

    var seedUrl = opts.seedUrl || SEED_URL;
    var state = {
      challengeId: null,
      seed: null,
      nonce: '',
      pending: null,
      liberando: false,
      /* powListo en true cuando no hay nada que resolver: con la prueba de
       * trabajo apagada —el estado con el que esto aterriza— el camino es
       * exactamente el de siempre, sin encolar nada. */
      powListo: true,
      powReady: null
    };

    /*
     * PRE-RESOLUCION: se arranca al RECIBIR el challenge, no al enviar. Con <=16
     * bits el nonce esta listo mucho antes de que una persona acabe de escribir,
     * asi que el caso normal no encola nada.
     */
    function arrancarPow(data) {
      var bits = (data && data.difficulty_bits) | 0;

      state.nonce = '';

      if (!bits) {
        state.powListo = true;
        state.powReady = null;

        return;
      }

      state.powListo = false;
      state.powReady = resolverPow(data).then(function (nonce) {
        state.nonce = nonce || '';
        state.powListo = true;

        return state.nonce;
      }, function () {
        state.powListo = true;

        return '';
      });
    }

    function ensure() {
      if (state.pending) return state.pending;

      state.pending = issue(seedUrl, opts.profile, opts.route).then(function (data) {
        state.challengeId = data.challenge_id;
        state.seed = data.seed;

        arrancarPow(data);

        if (data.csrf_token) {
          var input = form.querySelector('input[name="_token"]');
          if (input) input.value = data.csrf_token;
          var meta = document.querySelector('meta[name="csrf-token"]');
          if (meta) meta.setAttribute('content', data.csrf_token);
        }

        return data;
      }).catch(function () {
        /*
         * Fallo al emitir: no se bloquea al usuario. El servidor decidira, y en
         * los perfiles publicos hay intersticial como red de seguridad.
         */
        state.pending = null;

        return null;
      });

      return state.pending;
    }

    /*
     * El listener es {once:true} porque solo hace falta la primera vez. Por eso
     * hay que volver a ARMARLO cuando la pestana se restaura: sin esta funcion,
     * un `pageshow` persistido limpiaba el estado y dejaba el formulario sin
     * forma de pedir challenge nunca mas, es decir, enviando siempre sin token.
     */
    function armar() {
      form.addEventListener('focusin', ensure, { once: true });
    }

    function setToken() {
      if (!state.challengeId) return;

      var field = form.querySelector('input[name="antibot_token"]');

      if (!field) {
        field = document.createElement('input');
        field.type = 'hidden';
        field.name = 'antibot_token';
        form.appendChild(field);
      }

      field.value = encodeToken(state);
    }

    /*
     * Lo que hay que esperar antes de reenviar: primero el challenge y despues,
     * si lo hay, su prueba de trabajo. Ninguna de las dos promesas rechaza y las
     * dos tienen tope, asi que esta cadena no puede quedarse colgada — un
     * formulario que no envia nunca seria mucho peor que uno que envia sin
     * nonce, porque el segundo lo arregla el servidor y el primero no lo arregla
     * nadie.
     */
    function esperar() {
      return ensure().then(function () {
        return state.powReady || '';
      });
    }

    armar();

    /*
     * bfcache y pestanas restauradas: el challenge que hubiera puede haber
     * vencido (TTL de 8 min) o haberse consumido ya en la primera vida de la
     * pagina, asi que se descarta y se rearma.
     */
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;

      state.pending = null;
      state.challengeId = null;
      state.seed = null;
      state.nonce = '';
      state.powListo = true;
      state.powReady = null;
      state.liberando = false;
      armar();
    });

    form.addEventListener('submit', function (ev) {
      /*
       * Este es el envio que hemos relanzado nosotros al resolverse la promesa.
       * Sin esta bandera, cuando la emision falla el token sigue sin estar y el
       * handler vuelve a interceptar, a pedir seed y a reenviar: un bucle
       * infinito que convierte un fallo del endpoint en un flood contra el
       * propio endpoint.
       */
      if (state.liberando) {
        state.liberando = false;

        return;
      }

      if (state.challengeId && state.powListo) {
        setToken();

        return;
      }

      /*
       * Con el challenge en vuelo (o sin pedir), o con la prueba de trabajo a
       * medias, el envio se ENCOLA. Es el caso del auto-submit —el 2FA envia
       * solo al sexto digito— que de otro modo saldria sin token por carrera
       * con la promesa.
       *
       * 🚨 stopImmediatePropagation() ADEMAS de preventDefault(): preventDefault
       * detiene el envio del navegador, pero NO a los demas listeners de submit
       * de este mismo formulario. Sin esto, un formulario con manejador propio
       * lo ejecutaria ahora Y otra vez en el reenvio de abajo — envio duplicado,
       * que en un formulario de captacion son dos leads y en uno de pago, dos
       * cobros. Funciona porque este modulo se engancha ANTES que los demas
       * bundles (ver el final del fichero).
       */
      ev.preventDefault();
      ev.stopImmediatePropagation();

      esperar().then(function () {
        setToken();
        state.liberando = true;

        if (typeof form.requestSubmit === 'function') form.requestSubmit();
        else form.submit();
      });
    });
  }

  /*
   * Token suelto, para lo que NO es un submit de formulario.
   *
   * El reenvio del codigo 2FA es un fetch con su propio FormData construido a
   * mano: attach() no lo ve nunca. Sin esta via, esa ruta anotaria `would_block`
   * en el 100 % de los reenvios legitimos y su contador no diria nada — que es
   * justo el ruido que los overrides de tiempo minimo existen para quitar.
   *
   * Devuelve null si la emision falla: quien llama envia igual, porque los
   * perfiles de autenticacion son fail-open por invariante.
   *
   * Resuelve tambien la prueba de trabajo, con el mismo tope de 20 s: si no le
   * da tiempo, el token viaja sin nonce y decide el servidor. Un reenvio de
   * codigo que se quedara esperando seria peor que uno que llega sin resolver.
   */
  function issueToken(profile, route, seedUrl) {
    return issue(seedUrl || SEED_URL, profile, route).then(function (data) {
      return resolverPow(data).then(function (nonce) {
        return encodeToken({ challengeId: data.challenge_id, seed: data.seed, nonce: nonce });
      });
    }).catch(function () {
      return null;
    });
  }

  /*
   * Enganche por atributos: <form data-antibot-profile="..." data-antibot-route="...">
   *
   * Existe para que una vista no tenga que llevar un <script> propio. El
   * cableado por script inline en la vista NO servia en las pantallas de auth:
   * su unico @stack('scripts') vive en el <head> (partials/saheadnoindex), asi
   * que el script correria antes de que los formularios existan y antes de que
   * este mismo modulo se haya ejecutado — un no-op perfecto y silencioso.
   */
  function autoAttach(root) {
    var forms = (root || document).querySelectorAll('form[data-antibot-profile]');

    for (var i = 0; i < forms.length; i++) {
      attach(forms[i], {
        profile: forms[i].getAttribute('data-antibot-profile'),
        route: forms[i].getAttribute('data-antibot-route')
      });
    }
  }

  window.AntibotForm = { attach: attach, autoAttach: autoAttach, issueToken: issueToken };

  /*
   * Se engancha AQUI MISMO, sin esperar a DOMContentLoaded, y eso es
   * deliberado: este modulo viaja en un bundle de Vite, que es un
   * <script type="module"> y por tanto ya diferido — cuando se ejecuta, el HTML
   * esta parseado entero y los formularios existen. Esperar a
   * DOMContentLoaded llegaria DESPUES de que los demas bundles registren sus
   * propios listeners de submit, y el orden de registro es lo que decide si el
   * campo `antibot_token` esta puesto a tiempo para su FormData.
   *
   * La rama de DOMContentLoaded es solo la red para una carga no diferida; ahi
   * el orden no esta garantizado, por eso saas-auth.js llama ademas a attach()
   * explicitamente antes de registrar el suyo (attach es idempotente).
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      autoAttach();
    });
  } else {
    autoAttach();
  }
})();
